import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { PostHogProvider } from 'posthog-js/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const AppWithProviders = () => {
  const auth0Provider = (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_CLIENT_ID!}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        scope: "openid profile email",
        response_type: "code",
        prompt: "select_account"
      }}
      onRedirectCallback={async (appState, user) => {
        // Check onboarding status for authenticated users
        // IMPORTANT: Respect isNewUser flag - returning users (Login button) should access dashboard
        // even if onboarding check fails or returns incomplete
        try {
          if (user?.sub) {
            const isReturningUser = appState?.isNewUser === false;
            const jobId = appState?.jobId; // Extract jobId from appState
            
            console.log('[Auth0] User authenticated:', {
              sub: user.sub,
              email: user.email,
              isReturningUser,
              returnTo: appState?.returnTo,
              jobId: jobId || 'none'
            });

            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            
            // If jobId is present, link the user to the job posting
            if (jobId) {
              console.log('[Auth0] JOB ID DETECTED:', jobId);
              console.log('[Auth0] Linking user to job posting from external source...');
              
              try {
                const linkResponse = await fetch(`${apiUrl}/api/link-job-application`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    auth0Sub: user.sub,
                    jobId: jobId,
                    metadata: {
                      source: 'external_job_board',
                      userEmail: user.email,
                      userName: user.name,
                      timestamp: new Date().toISOString(),
                      referrer: document.referrer || 'direct'
                    }
                  })
                });

                if (linkResponse.ok) {
                  const linkData = await linkResponse.json();
                  console.log('[Auth0] Successfully linked user to job posting:', {
                    jobId,
                    applicationId: linkData.application?.id,
                    auth0Sub: user.sub
                  });
                  console.log('[Auth0] 🎉 Job application tracking complete!');
                } else {
                  console.error('[Auth0] Failed to link user to job posting:', await linkResponse.text());
                }
              } catch (linkError) {
                console.error('[Auth0] Error linking user to job posting:', linkError);
                // Don't block authentication flow if job linking fails
              }
            } else {
              console.log('[Auth0] No job ID in appState, proceeding with normal flow');
            }

            // Continue with normal onboarding check
            const response = await fetch(`${apiUrl}/api/check-onboarding-status`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userData: {
                  sub: user.sub,
                  email: user.email,
                }
              })
            });

            if (response.ok) {
              const data = await response.json();
              console.log('[Auth0] Onboarding check result:', {
                hasCompletedOnboarding: data.hasCompletedOnboarding,
                isReturningUser
              });
              
              if (!data.hasCompletedOnboarding) {
                // User hasn't completed onboarding
                // BUT: If they clicked "Login" (returning user), allow dashboard access
                // This prevents existing users from being stuck in onboarding loop
                if (isReturningUser) {
                  console.log('[Auth0] Onboarding incomplete but user clicked Login - allowing dashboard access');
                  window.location.href = appState?.returnTo || '/dashboard';
                  return;
                }
                // New user signup - redirect to onboarding
                console.log('[Auth0] New user has not completed onboarding, redirecting to /onboarding');
                window.location.href = '/onboarding';
                return;
              } else {
                // User has completed onboarding
                // If returnTo is /onboarding, ignore it and go to dashboard instead
                // (this happens when user clicks Sign Up but then logs in with existing account)
                const requestedDestination = appState?.returnTo;
                const destination = requestedDestination === '/onboarding' ? '/dashboard' : (requestedDestination || '/dashboard');
                console.log('[Auth0] User has completed onboarding, redirecting to:', destination);
                window.location.href = destination;
                return;
              }
            } else {
              console.warn('[Auth0] Failed to check onboarding status');
              // If check fails and this is explicitly a login (returning user intent),
              // allow them to dashboard. Otherwise, redirect to onboarding for safety
              if (isReturningUser) {
                console.log('[Auth0] Check failed but login detected, allowing dashboard access');
                window.location.href = appState?.returnTo || '/dashboard';
              } else {
                console.log('[Auth0] Check failed for new/unknown user, redirecting to onboarding');
                window.location.href = '/onboarding';
              }
              return;
            }
          }
        } catch (error) {
          console.error('[Auth0] Error checking onboarding status:', error);
          // If there's an error and this is explicitly a login, allow dashboard access
          // Otherwise redirect to onboarding for safety
          const isReturningUser = appState?.isNewUser === false;
          if (isReturningUser) {
            console.log('[Auth0] Error but login detected, allowing dashboard access');
            window.location.href = appState?.returnTo || '/dashboard';
          } else {
            console.log('[Auth0] Error for new/unknown user, redirecting to onboarding');
            window.location.href = '/onboarding';
          }
          return;
        }

        // Fallback if user info is missing
        window.location.href = '/';
      }}
    >
      <App />
    </Auth0Provider>
  );

  // Only wrap with PostHogProvider if API key is available
  if (process.env.REACT_APP_POSTHOG_KEY) {
    return (
      <PostHogProvider
        apiKey={process.env.REACT_APP_POSTHOG_KEY}
        options={{
          api_host: process.env.REACT_APP_POSTHOG_HOST,
          defaults: '2025-05-24',
          capture_exceptions: true,
          debug: process.env.NODE_ENV === 'development',
        }}
      >
        {auth0Provider}
      </PostHogProvider>
    );
  }

  return auth0Provider;
};

root.render(
  // StrictMode disabled temporarily to prevent double API calls during development
  // Re-enable in production or when rate limiting is optimized
  // <React.StrictMode>
    <AppWithProviders />
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();